'use strict'

/* HOW TO USE THIS HOOK
import React from 'react'
import {useGiftCardLogic} from '../../hooks/use-gift-card-logic'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useGiftCertificate, usePaymentMethodsForBasket} from '@salesforce/commerce-sdk-react'

const MyNewPage = () => {
    const {
        checkIfThereAnyGCInBasket,
        checkIfGCExist,
        checkIfGCExpired,
        checkIfThereAnyBalance,
        checkIfGCAndBasketCurrenciesAreSame,
        checkIfGCAvailableAsPaymentMethod
    } = useGiftCardLogic()

    const {data: basket} = useCurrentBasket()

    if (basket) {
        console.log(basket)
        console.log(checkIfAnyGCPaymentInstrumentApplied(basket))
    }

    const gcCode = 'YIRWLKVPZYVJQYCP'
    const {data: giftCertificate, isLoading: isGCLoading} = useGiftCertificate({
        body: {giftCertificateCode: gcCode}
    })

    if (!isGCLoading) {
        console.log('Does GC Exist:', checkIfGCExist(giftCertificate))
        console.log('Does GC expired:', checkIfGCExpired(giftCertificate))
        console.log('Is there any balance left:', checkIfThereAnyBalance(giftCertificate))
    }

    if (!isGCLoading && basket) {
        console.log(basket)
        console.log(giftCertificate)
        console.log(
            'Check if basket and gc currencies the same: ',
            checkIfGCAndBasketCurrenciesAreSame(basket, giftCertificate)
        )
    }

    if (basket) {
        const anyGiftCert = checkIfThereAnyGCInBasket(basket)
        console.log('Any gift certificate in basket: ', anyGiftCert)
    }

    const {data: basketPaymentMethods} = usePaymentMethodsForBasket({
        parameters: {
            basketId: basket?.basketId
        }
    })

    if (basketPaymentMethods && basketPaymentMethods.applicablePaymentMethods) {
        console.log(basketPaymentMethods)
        console.log(
            'Is GC available as a payment method: ',
            checkIfGCAvailableAsPaymentMethod(basketPaymentMethods.applicablePaymentMethods)
        )
    }

    return <h1>TEST</h1>
}

MyNewPage.getTemplateName = () => 'MyNewPage'

export default MyNewPage

*/

// Constants
import {GC_PAYMENT_METHOD_ID, COUNTRIES_USING_RHT_PRICEBOOK} from '../util/constants'

// Utils
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Provides logic related to gift card operations within a shopping basket.
 * This module includes various utility functions to check the presence, validity,
 * and applicability of gift cards in a given basket context.
 */
export const useGiftCardLogic = () => {
    const {app: appConfig} = getConfig()

    /**
     * Checks if there are any gift certificate items present in the given basket.
     * A customer cannot buy and redeem a gift card in the same order.
     * @param {Object} basket - The basket object to be checked for gift certificate items.
     * @returns {boolean} - Returns true if there is at least one gift certificate item in the basket, otherwise returns false.
     */
    const checkIfThereAnyGCInBasket = (basket) => {
        let isThereAnyGC = false

        if (basket?.giftCertificateItems?.length > 0) {
            isThereAnyGC = true
        }

        return isThereAnyGC
    }
    /**
     * Checks if any gift card payment instrument is applied to the given basket.
     * A customer cannot buy and redeem a gift card in the same order case
     * This function iterates over the payment instruments associated with the
     * provided basket and determines if any of them use the gift card payment
     * method identified by the constant `GC_PAYMENT_METHOD_ID`.
     *
     * @param {Object} basket - The basket object containing payment instruments.
     * @returns {boolean} - Returns true if any gift card payment instrument is
     *                      applied, otherwise returns false.
     */
    const checkIfAnyGCPaymentInstrumentApplied = (basket) => {
        let isAnyGCInstrumentApplied = false

        basket?.paymentInstruments?.forEach((instrument) => {
            if (instrument.paymentMethodId === GC_PAYMENT_METHOD_ID) {
                isAnyGCInstrumentApplied = true
            }
        })

        return isAnyGCInstrumentApplied
    }
    /**
     * Checks if a gift certificate exists.
     * “This gift certificate number doesn’t exist.” case
     * @param {Object|null} gc - The gift certificate object to check.
     * @returns {boolean} - Returns true if the gift certificate exists, otherwise false.
     */
    const checkIfGCExist = (gc) => {
        return gc ? true : false
    }
    /**
     * Checks if a gift card is expired based on its expiration date.
     * “This gift certificate number has expired.” case
     * @param {Object} gc - The gift card object to be checked.
     * @param {string} gc.c_expirationDate - The expiration date of the gift card in a valid date string format.
     * @returns {boolean} - Returns true if the gift card is expired, false otherwise.
     */
    const checkIfGCExpired = (gc) => {
        let isGCExpired = false

        // If expiration date does not exist
        if (!gc.c_expirationDate) {
            return false
        }

        if (new Date(gc.c_expirationDate) <= Date.now()) {
            isGCExpired = true
        }

        return isGCExpired
    }
    /**
     * Checks if a gift card has any remaining balance.
     * “There is nothing left on this gift certificate.” (when the balance is 0) case
     * @param {Object} gc - The gift card object to be checked.
     * @param {number} gc.balance - The current balance of the gift card.
     * @returns {boolean} - Returns true if the gift card has a balance greater than zero, otherwise false.
     */
    const checkIfThereAnyBalance = (gc) => {
        return gc.balance > 0 ? true : false
    }
    /**
     * Checks if the currency of the basket matches the currency code of the gift certificate.
     * "A gift card can only be redeemed in the same currency" case
     * @param {Object} basket - The basket object containing the currency information.
     * @param {Object} gc - The gift certificate object containing the currency code.
     * @returns {boolean} - Returns true if the basket currency matches the gift certificate currency code, otherwise false.
     */
    const checkIfGCAndBasketCurrenciesAreSame = (basket, gc) => {
        return basket.currency === gc.c_giftCertificateCurrencyCode
    }
    /**
     * Checks if a Gift Card is available as a payment method.
     *
     * This function iterates over a list of applicable payment methods to determine
     * if a Gift Card payment method is present. It returns a boolean indicating the
     * presence of the Gift Card payment method.
     *
     * @param {Array} applicablePaymentMethods - An array of payment method objects,
     *                                           each containing an 'id' property.
     * @returns {boolean} - Returns true if the Gift Card payment method is available,
     *                      otherwise returns false.
     */
    const checkIfGCAvailableAsPaymentMethod = ({
        basket,
        applicablePaymentMethods,
        conversionRate,
        shipToCountry
    }) => {
        let isGCExistAsPaymentMethod = false
        applicablePaymentMethods?.forEach((method) => {
            if (method.id === GC_PAYMENT_METHOD_ID) {
                isGCExistAsPaymentMethod = true
            }
        })

        const isCurrencySupported = isGCAvailableForCurrency(
            conversionRate?.paymentCurrencyCode || basket?.currency
        )
        const isCountrySupported = isGCAvailableForCurrentCountry(shipToCountry.id)

        return isGCExistAsPaymentMethod && isCurrencySupported && isCountrySupported
    }
    /**
     * Calculates the total amount of gift card credits applied to a given basket.
     *
     * @param {Object} basket - The basket object containing payment instruments.
     * @returns {number} The total amount of gift card credits applied to the basket.
     */
    const getTotalGiftcardCreditsApplied = (basket) => {
        return (
            basket?.paymentInstruments
                ?.filter((instrument) => instrument.paymentMethodId === GC_PAYMENT_METHOD_ID)
                ?.reduce((acc, gc) => acc + gc.amount, 0) ?? 0
        )
    }
    /**
     * Retrieves all gift card payment instruments from the provided basket.
     *
     * @param {Object} basket - The basket object containing payment instruments.
     * @returns {Array} An array of payment instruments that use the gift card payment method.
     */
    const getGiftcardPaymentInstruments = (basket) => {
        return (
            basket?.paymentInstruments?.filter(
                (instrument) => instrument.paymentMethodId === GC_PAYMENT_METHOD_ID
            ) ?? []
        )
    }
    /**
     * Checks if the gift card is alreay in the payment instruments
     *
     * @param {Object} basket - The basket object containing payment instruments
     * @param {Object} gcObj - The gift certificate object
     * @returns {boolean}
     */
    const checkIfGiftcardIsInPaymentInstruments = (basket, gcObj) => {
        return (
            basket?.paymentInstruments?.findIndex(
                (inst) =>
                    inst.paymentMethodId === GC_PAYMENT_METHOD_ID &&
                    inst.maskedGiftCertificateCode === gcObj.maskedGiftCertificateCode &&
                    inst.c_paymentInstrumentCurrencyCode === gcObj.c_giftCertificateCurrencyCode
            ) > -1
        )
    }
    /**
     * Determines if the Gift Card (GC) is available for the specified country.
     *
     * This function checks whether the current country is part of the countries
     * using the RHT price book. If the country is not in the list, it implies
     * that the Gift Card is available for that country.
     *
     * @param {string} currentCountryID - The identifier of the current country.
     * @returns {boolean} - Returns true if the Gift Card is available for the
     *                      current country; otherwise, false.
     */
    const isGCAvailableForCurrentCountry = (currentCountryID) => {
        return !COUNTRIES_USING_RHT_PRICEBOOK.includes(currentCountryID)
    }

    /**
     * Determines if the Gift Card is available for the specified currency.
     *
     * @param {string} currency - The currency id.
     * @returns {boolean} - Returns true if the Gift Card is available for the
     *                      current currency; otherwise, false.
     */
    const isGCAvailableForCurrency = (currency) => {
        return appConfig?.GC_PREDEFINED_AMOUNTS?.amountsPerCurrency[currency] !== undefined
    }

    /**
     * Checks for a currency mismatch between the provided gift card(s) and the specified currency.
     *
     * This function determines if there is a discrepancy between the currency code of the gift card(s)
     * and the provided currency. It can handle both single gift card object and arrays of gift card objects.
     *
     * @param {Object|Object[]} gc - A single gift card object or an array of gift card objects. Works with Gift
     *                               Card line items and Gift Card Payment Instruments.
     * @param {string} currency - The currency code to compare against the gift card(s) currency code.
     * @returns {boolean} Returns `true` if there is a currency mismatch, otherwise `false`.
     */
    const checkGiftCardCurrencyMismatch = (gc, currency) => {
        if (Array.isArray(gc)) {
            return gc.some((gcItem) => {
                return (
                    currency !==
                    (gcItem.c_giftCertificateCurrencyCode || gcItem.c_paymentInstrumentCurrencyCode)
                )
            })
        } else {
            return (
                currency !==
                (gc.c_giftCertificateCurrencyCode || gc.c_paymentInstrumentCurrencyCode)
            )
        }
    }

    /**
     * Calculates the total remaining balance across all gift cards.
     * @function
     * @param {Array<Object>} giftCards - An array of gift card objects, each containing balance information.
     * @returns {number} The sum of the remaining balances on all provided gift cards.
     */
    const getTotalGCRemainingBalance = (basket) => {
        return (
            basket?.paymentInstruments
                ?.filter((instrument) => instrument.paymentMethodId === GC_PAYMENT_METHOD_ID)
                ?.reduce((acc, gc) => acc + Number(gc.c_giftCardRemainingBalance), 0) ?? 0
        )
    }

    return {
        checkGiftCardCurrencyMismatch,
        checkIfAnyGCPaymentInstrumentApplied,
        checkIfGCAndBasketCurrenciesAreSame,
        checkIfGCAvailableAsPaymentMethod,
        checkIfGCExist,
        checkIfGCExpired,
        checkIfGiftcardIsInPaymentInstruments,
        checkIfThereAnyBalance,
        checkIfThereAnyGCInBasket,
        getGiftcardPaymentInstruments,
        getTotalGiftcardCreditsApplied,
        isGCAvailableForCurrency,
        isGCAvailableForCurrentCountry,
        getTotalGCRemainingBalance
    }
}
